// src/modules/assetManagement/utils.ts
import type { AssetKind, AssetStatus, Crumb, StorageState } from "./types";

export const BASE = "/customer-manager";
export const ROUTES = {
  dashboard: `${BASE}/dashboard`,
  library: `${BASE}/library`,
  libraryEvents: `${BASE}/library/events`,
  libraryProducts: `${BASE}/library/products`,
  libraryAssets: `${BASE}/library/assets`,
  management: `${BASE}/asset-management`,
  stats: `${BASE}/stats`,
  product: (id: string) => `${BASE}/products/${id}`,
  asset: (id: string) => `${BASE}/library/assets/${id}`,
  managedAsset: (id: string) => `${BASE}/asset-management/assets/${id}`,
};

export const MAX_FILE_BYTES = 10 * 1024 ** 3;
export const MAX_ATTEMPTS = 3;

/** Accepted upload types (mirrors the current ingestion allow-list) */
export const ACCEPTED_EXTENSIONS: Record<string, AssetKind> = {
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image", svg: "image",
  mp4: "video", mov: "video", avi: "video", mpeg: "video", webm: "video",
  mp3: "audio", wav: "audio", ogg: "audio",
  pdf: "document", doc: "document", docx: "document", xls: "document", xlsx: "document",
  txt: "document", json: "document", zip: "document",
};

export const extensionOf = (name: string) => name.split(".").pop()?.toLowerCase() ?? "";

export const formatBytes = (bytes: number) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

export const formatDuration = (sec?: number | null) => {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

export const formatDate = (iso?: string | null, withTime = false) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
};

export const STATUS_META: Record<AssetStatus, { label: string; className: string }> = {
  completed: { label: "Ready", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  queued: { label: "Queued", className: "bg-slate-100 text-slate-600 border-slate-200" },
  transcoding: { label: "Processing", className: "bg-blue-50 text-blue-700 border-blue-200" },
  failed: { label: "Failed", className: "bg-red-50 text-red-700 border-red-200" },
  rejected: { label: "Rejected", className: "bg-orange-50 text-orange-700 border-orange-200" },
};

export const STORAGE_META: Record<StorageState, { label: string; className: string }> = {
  standard: { label: "Active", className: "bg-white text-slate-600 border-slate-200" },
  archived: { label: "Archived", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  restoring: { label: "Restoring", className: "bg-amber-50 text-amber-700 border-amber-200" },
};

/** Colours follow the workflow PDF: Events blue, Products amber, Assets green */
export const ENTITY = {
  event: { chip: "bg-blue-50 text-blue-700 border-blue-200", solid: "#2563EB" },
  product: { chip: "bg-amber-50 text-amber-800 border-amber-200", solid: "#B45309" },
  asset: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", solid: "#15803D" },
};

/** Router state used for one-step-back breadcrumbs */
export interface TrailState {
  trail?: Crumb[];
}

export const trailFrom = (state: unknown): Crumb[] =>
  ((state as TrailState | null)?.trail ?? []).filter(Boolean);
